import React, { useState, SyntheticEvent, ReactNode } from 'react';
import { ButtonBase as IconButton } from '@fenderdigital/ui/Button';
import styled from 'styled-components';
import usePlayUser from '@fenderdigital/custom-hooks/hooks/usePlayUser';
import * as FavoritesTypes from '../../routes/favorites/types';
import { FavoriteDataProps } from '../../routes/favorites/Favorites';

interface EllipsisDropdownProps {
  activityType: string;
  dataId?: string;
  favoritesKeys: FavoritesTypes.FavoritesKeys;
  children: ReactNode;
  sourcePath: string;
  setFavorite: (
    favoritesId: string,
    activityType: string,
    sourcePath: string,
    favoriteData?: FavoriteDataProps,
    userID?: string,
  ) => void;
  unsetFavorite: (
    favoritesId: string,
    activityType: string,
    sourcePath: string,
    favoriteData?: FavoriteDataProps,
    userID?: string,
  ) => void;
  favoritesId?: string;
  favoriteData?: FavoriteDataProps;
}

const EllipsisDropdownStyled = styled.div`
  display: flex;
  position: relative;

  button {
    min-width: 36px;
    font-size: ${({ theme: { fontSizes } }): string => fontSizes.f3};

    &[aria-expanded='false'] > div {
      display: none;
    }
  }
`;

const Menu = styled.div`
  z-index: 7;
  position: absolute;
  right: 0;
  top: 30px;
  display: block;
  box-shadow: 2px 2px 8px 0 rgb(0 0 0 / 20%);
  border: 1px solid ${({ theme: { colors } }): string => colors.moonGray};
  background: ${({ theme: { colors } }): string => colors.white};
`;

const EllipsisDropdown: React.FC<EllipsisDropdownProps> = ({
  activityType = '',
  dataId = 'lesson-ellipsis',
  favoritesKeys,
  sourcePath,
  setFavorite,
  unsetFavorite,
  favoritesId = '',
  children,
  favoriteData = {},
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { user } = usePlayUser();

  const isFavorited = favoritesId in favoritesKeys;
  const handleFavorite = (event: SyntheticEvent): void => {
    event.preventDefault();

    if (favoritesId in favoritesKeys) {
      unsetFavorite(favoritesId, activityType, sourcePath, favoriteData, user?.userID);
    } else {
      setFavorite(favoritesId, activityType, sourcePath, favoriteData, user?.userID);
    }
  };

  const handleClick = (event: SyntheticEvent): void => {
    event.stopPropagation();
    event.preventDefault();
    setIsOpen(!isOpen);
  };

  const handleMouseOver = (): void => {
    setIsOpen(true);
  };

  const handleMouseOut = (): void => {
    setIsOpen(false);
  };

  return (
    <EllipsisDropdownStyled data-id="ellipsis-dropdown-container">
      <IconButton
        size="small"
        variant="ghost"
        hoverColor="white"
        onClick={handleFavorite}
        data-id="favorite-icon-lesson"
        aria-label="Favorite"
        aria-pressed={isFavorited}
      >
        <i className={isFavorited ? 'icon-heart-filled' : 'icon-heart'} aria-hidden />
      </IconButton>
      <IconButton
        data-id={dataId}
        size="small"
        variant="ghost"
        hoverColor="white"
        aria-label="Menu"
        aria-expanded={isOpen}
        id="menu-button"
        aria-controls="mark-as-complete-menu"
        onClick={handleClick}
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
      >
        <i className="icon-elipsis-alt f5 f4-ns light-silver" aria-hidden />
        <Menu
          data-id="mark-as-complete-modal"
          id="mark-as-complete-menu"
          aria-labelledby="menu-button"
        >
          {React.Children.map(children, child =>
            React.cloneElement(child as React.ReactElement, {
              handleCloseEllipsis: handleMouseOut,
            }),
          )}
        </Menu>
      </IconButton>
    </EllipsisDropdownStyled>
  );
};

export default EllipsisDropdown;
